import type { Dispatch, SetStateAction } from 'react'
import React from 'react'
import { Link } from 'react-router-dom'
import md5 from 'md5'

import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { faBuilding, faBuildingFlag, faBusinessTime, faCircleNodes, faDiagramProject, faFileContract, faFileInvoice, faGears, faLock, faMapLocationDot, faNetworkWired, faPlugCircleXmark, faReceipt, faRecycle, faRoute, faScrewdriverWrench, faSignsPost, faSquarePlus, faTableColumns, faUser, faUserGroup, faUserTie } from '@fortawesome/free-solid-svg-icons'

import { faBallotCheck, faBarcode, faBells, faBox, faBoxes, faChartLine, faChessClock, faClipboardList, faExchange, faHistory, faMagnifyingGlassLocation, faPhoneOffice, faShieldCheck, faTrademark, faTrianglePersonDigging, faTruck, faTruckMoving, faWarehouse } from '@fortawesome/pro-solid-svg-icons'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useUser } from '../../../providers/UserProvider'
import { checkPermission } from '../../../util/checkPermission'
import MenuItem from '../../../components/MenuItem'
import logo from './logo512.png'

import './MenuSidebar.css'

export interface IMenuItem {
  name: string
  permission?: string
  exact: boolean
  path?: string
  icon?: IconDefinition | IconProp
  children?: Array<IMenuItem>
}

export const MENU: IMenuItem[] = [
  {
    name: 'Dashboard',
    icon: faTableColumns,
    path: '/',
    exact: true,
  },
  {
    name: 'BAG',
    exact: false,
    permission: '/frontend/modules/bag',
    icon: faSignsPost,
    children: [
      {
        icon: faMapLocationDot,
        name: 'Alle Adressen',
        exact: false,
        path: '/bag/adressen',
      },
      {
        icon: faMagnifyingGlassLocation,
        name: 'BU Finder',
        exact: false,
        path: '/bag/bufinder',
      },
      {
        icon: faPlugCircleXmark,
        name: 'Ingetrokken Adressen',
        exact: false,
        permission: '/modules/netwerkbeheer/aansluitingen/revoked-bag',
        path: '/bag/revoked',
      },
    ],
  },
  {
    name: 'Netwerkbeheer',
    exact: false,
    permission: '/frontend/modules/netwerkbeheer',
    icon: faRoute,
    children: [
      {
        name: 'Aansluitingen',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/aansluitingen',
        icon: faBuildingFlag,
        path: '/netwerkbeheer/aansluitingen',
      },
      {
        name: 'Interconnects',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/interconnects',
        icon: faCircleNodes,
        path: '/netwerkbeheer/interconnects',
      },
      {
        name: 'EVC\'s',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/evcs',
        icon: faNetworkWired,
        path: '/netwerkbeheer/evcs',
      },
      {
        name: 'Apparaten',
        exact: false,
        permission: '/frontend/modules/netwerkbeheer/apparaten',
        icon: faNetworkWired,
        path: '/netwerkbeheer/apparaten',
      },
      {
        name: 'IP Adressen',
        exact: false,
        permission: '/modules/netwerkbeheer/ipadressen',
        icon: faNetworkWired,
        path: '/netwerkbeheer/ip',
      },
    ],
  },
  {
    name: 'VOIP',
    exact: false,
    permission: '/frontend/modules/voip',
    icon: faPhoneOffice as IconProp,
    children: [
      {
        name: 'Klanten',
        exact: false,
        permission: '/frontend/modules/voip/klanten',
        icon: faBuilding,
        path: '/voip/klanten',
      },
    ],
  },
  {
    name: 'Klanten',
    exact: false,
    permission: '/frontend/modules/klanten',
    icon: faUserTie,
    path: '/customers',
  },
  {
    name: 'Projecten',
    permission: '/frontend/modules/projecten',
    exact: false,
    icon: faDiagramProject,
    children: [
      {
        name: 'Alle Projecten',
        exact: false,
        path: '/projecten',
      },
      {
        name: 'Proposities',
        exact: false,
        icon: faFileContract,
        path: '/propositions',
        permission: '/frontend/modules/projects/propositions',
      },
    ],
  },
  {
    name: 'Provisioning',
    exact: false,
    permission: '/frontend/modules/provisioning',
    icon: faGears,
    children: [
      {
        name: 'Routers',
        exact: false,
        icon: faRoute,
        path: '/provisioning/routers',
      },
    ],
  },
  {
    name: 'RADIUS',
    exact: false,
    permission: '/modules/radius',
    icon: faShieldCheck as IconProp,
    children: [
      {
        name: 'Dashboard',
        exact: false,
        permission: '/modules/radius',
        icon: faChartLine as IconProp,
        path: '/radius/dashboard',
      },
      {
        name: 'User Lookup',
        exact: false,
        permission: '/modules/radius',
        icon: faUser,
        path: '/radius/user-lookup',
      },
      {
        name: 'Active Sessions',
        exact: false,
        permission: '/modules/radius/accounting',
        icon: faCircleNodes,
        path: '/radius/sessions/active',
      },
      {
        name: 'Session History',
        exact: false,
        permission: '/modules/radius/accounting',
        icon: faHistory as IconProp,
        path: '/radius/sessions/history',
      },
      {
        name: 'NAS Management',
        exact: false,
        permission: '/modules/radius/nas',
        icon: faNetworkWired,
        path: '/radius/nas',
      },
      {
        name: 'Authentication Logs',
        exact: false,
        permission: '/modules/radius/postauth',
        icon: faLock,
        path: '/radius/auth-logs',
      },
      {
        name: 'Reports',
        exact: false,
        permission: '/modules/radius',
        icon: faClipboardList as IconProp,
        path: '/radius/reports',
      },
    ],
  },
  {
    name: 'Meldingen',
    exact: false,
    permission: '/frontend/modules/meldingen',
    icon: faBells,
    children: [
      {
        name: 'Onderhoud',
        exact: false,
        permission: '/modules/meldingen/onderhoud',
        icon: faTrianglePersonDigging,
        path: '/meldingen/onderhoud',
      },
      {
        name: 'Storingen',
        exact: false,
        permission: '/modules/meldingen/storing',
        icon: faPlugCircleXmark,
        path: '/meldingen/storingen',
      }
    ]
  },
  {
    name: 'Voorraad',
    exact: false,
    permission: '/frontend/modules/inventory',
    icon: faBoxes, // top-level icon representing inventory
    children: [
      {
        name: 'Warenhuis',
        exact: false,
        permission: '/frontend/modules/inventory/warehouse',
        icon: faWarehouse, // icon representing warehouses/storage
        path: '/voorraad/warenhuizen',
      },
      {
        name: 'Merken',
        exact: false,
        permission: '/frontend/modules/inventory/brands',
        icon: faTrademark, // icon representing brands
        path: '/voorraad/merken',
      },
      {
        name: 'Leveranciers',
        exact: false,
        permission: '/frontend/modules/inventory/vendors',
        icon: faTruck, // icon representing vendors/suppliers
        path: '/voorraad/leveranciers',
      },
      {
        name: 'Producten',
        exact: false,
        permission: '/frontend/modules/inventory/products',
        icon: faBox, // icon representing a product/item
        path: '/voorraad/producten',
      },
      {
        name: 'Inkooporders',
        exact: false,
        permission: '/frontend/modules/inventory/purchaseorder',
        icon: faFileInvoice, // icon representing orders/documents
        path: '/voorraad/inkooporders',
      },
      {
        name: 'Mutaties',
        exact: false,
        permission: '/frontend/modules/inventory/mutations',
        icon: faExchange, // icon representing changes/transactions
        path: '/voorraad/mutaties',
      },
      {
        name: 'Tracking',
        exact: false,
        permission: '/frontend/modules/inventory/tracking',
        icon: faTruckMoving, // icon representing movement/tracking
        path: '/voorraad/tracking',
      },

    ],
  },
  {
    name: 'Administratie',
    exact: false,
    permission: '/frontend/modules/administratie',
    icon: faReceipt,
    children: [
      {
        name: 'Werkbonnen',
        exact: false,
        permission: '/frontend/modules/administratie/werkbonnen',
        icon: faBallotCheck,
        path: '/administratie/werkbonnen',
      },
      {
        name: 'Urenregistratie',
        exact: false,
        permission: '/frontend/modules/administratie/urenregistratie',
        icon: faBusinessTime,
        path: '/administratie/urenregistratie',
      },
    ],
  },
  {
    name: 'Tools',
    exact: false,
    permission: '/frontend/modules/tools',
    icon: faScrewdriverWrench,
    children: [
      {
        name: 'Belkosten2Exact',
        exact: false,
        permission: '/frontend/modules/tools/belkosten2exact',
        icon: faFileInvoice,
        path: '/tools/belkosten2exact',
      },
      {
        name: 'Gpon Tool',
        exact: false,
        permission: '/frontend/modules/tools/gponpreprocessor',
        icon: faSquarePlus,
        path: '/tools/gponpreprocessor',
      },
    ],
  },
  {
    name: 'Kaart',
    exact: false,
    permission: '/frontend/modules/kaart',
    icon: faMapLocationDot,
    path: '/kaart',
  },
  {
    name: 'Instellingen',
    exact: false,
    permission: '/frontend/modules/instellingen',
    icon: faGears,
    path: '/settings',
    children: [
      {
        name: 'Barcode Scanner',
        exact: false,
        permission: '/frontend/modules/instellingen/scanner',
        icon: faBarcode,
        path: '/settings/scanner',
      },
      {
        name: 'Outer VLANs',
        exact: false,
        permission: '/frontend/modules/instellingen/outervlan',
        icon: faNetworkWired,
        path: '/settings/outervlan',
      },
      {
        name: 'Toegangsbeheer',
        exact: false,
        permission: '/frontend/modules/instellingen/gebruikers',
        icon: faLock,
        path: '/settings/toegang',
        children: [
          {
            name: 'Gebruikers',
            exact: false,
            permission: '/frontend/modules/instellingen/gebruikers',
            icon: faUser,
            path: '/settings/toegang/gebruikers',
          },
          {
            name: 'Groepen',
            exact: false,
            permission: '/frontend/modules/instellingen/groepen',
            icon: faUserGroup,
            path: '/settings/toegang/groepen',
          },
        ],
      },
    ],
  },
]

interface MenuSidebarProps {
  sidebar: {
    get: boolean
    set: Dispatch<SetStateAction<boolean>>
  }
}

function MenuSidebar(props: MenuSidebarProps) {
  // const user = useSelector((state: any) => state.auth.currentUser);
  // const sidebarSkin = useSelector((state: any) => state.ui.sidebarSkin);
  // const menuItemFlat = useSelector((state: any) => state.ui.menuItemFlat);
  // const menuChildIndent = useSelector((state: any) => state.ui.menuChildIndent);
  const { user, permissions } = useUser()

  const isDev = process.env.NODE_ENV === 'development'
  const sidebarSkin = isDev ? 'sidebar-dark-red' : 'sidebar-dark-blue'
  const menuItemFlat = false
  const menuChildIndent = true

  return (
    <aside className={`main-sidebar elevation-4 ${sidebarSkin}`}>
      <Link to="/" className="brand-link">
        <img
          src={logo}
          alt="AdminLTE Logo"
          className="brand-image img-circle elevation-3"
          style={{ opacity: '.8' }}
        />
        <span className="brand-text font-weight-light">Datafiber.Network</span>
      </Link>
      <div className="sidebar">
        <div className="user-panel mt-3 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src={`https://www.gravatar.com/avatar/${md5(user.email ? user.email : 'operations@datafiber.nl')}?s=160&d=retro&r=pg`}
              className="img-circle elevation-2"
              alt="User"
            />
          </div>
          <div className="info">
            <Link to="/profile" className="d-block">
              {user.firstName}
              {' '}
              {user.lastName}
            </Link>
          </div>
        </div>
        <nav className="mt-2" style={{ overflowY: 'hidden' }}>
          <ul
            className={`nav nav-pills nav-sidebar flex-column${menuItemFlat ? ' nav-flat' : ''
              }${menuChildIndent ? ' nav-child-indent' : ''}`}
            role="menu"
          >
            {MENU.map((menuItem: IMenuItem) => {
              if (menuItem.permission && checkPermission(menuItem.permission, 'read', permissions) === false) {
                return null
              }

              return (
                <MenuItem key={menuItem.name} menuItem={menuItem} />
              )
            })}
          </ul>
        </nav>
      </div>
    </aside>
  )
}

export default MenuSidebar
